import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GROUP_LABELS = {
  locations: 'Locations', trades: 'Trades', wbs: 'WBS',
  activities: 'Activities', materials: 'Materials', drawings: 'Drawings',
};

/**
 * Project-scoped construction search. Tenant/project isolation enforced server-side.
 * onSelect(item, group) fires when a result is picked.
 */
const ConstructionSearch = ({ projectId, onSelect, placeholder = 'Search buildings, floors, zones, trades, drawings…', testId = 'construction-search' }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/projects/${projectId}/construction-search`, { params: { q: q.trim() } });
        setResults(r.data || {});
        setOpen(true);
      } catch (e) { setResults({}); } finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, projectId]);

  const groups = results ? Object.entries(results).filter(([, arr]) => (arr || []).length > 0) : [];
  const totalCount = groups.reduce((s, [, arr]) => s + arr.length, 0);

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 py-2">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder={placeholder}
          className="flex-1 text-sm outline-none bg-transparent"
          data-testid={`${testId}-input`}
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {q && !loading && <button onClick={() => { setQ(''); setResults(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
      </div>

      {open && results && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-auto" data-testid={`${testId}-results`}>
          {totalCount === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400" data-testid={`${testId}-empty`}>No matches</div>
          ) : (
            groups.map(([group, arr]) => (
              <div key={group}>
                <div className="px-3 py-1 text-[10px] uppercase font-semibold text-slate-400 bg-slate-50 sticky top-0">{GROUP_LABELS[group] || group}</div>
                {arr.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setOpen(false); onSelect && onSelect(item, group); }}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between"
                    data-testid={`${testId}-result`}
                  >
                    <span className="text-sm text-slate-800">{item.name || item.fileName}</span>
                    {item.kind && <span className="text-[10px] uppercase text-slate-400">{item.kind}</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ConstructionSearch;
